import type { Hip3Market, AggregatedQuote, ExecutionRoute, SmartRoute, OrderSplit } from '../types';
import { hyperliquidClient } from './hyperliquid';

export class QuoteAggregator {
  private markets: Hip3Market[] = [];

  // Set available markets (would typically fetch this from discovery)
  setMarkets(markets: Hip3Market[]) {
    this.markets = markets;
  }

  // Get all markets for a specific base asset (e.g., all GOLD markets)
  getMarketsForAsset(baseAsset: string): Hip3Market[] {
    return this.markets.filter((m) => m.baseAsset === baseAsset);
  }

  // Aggregate quotes for a specific asset across all providers
  async aggregateQuotes(baseAsset: string): Promise<AggregatedQuote | null> {
    const markets = this.getMarketsForAsset(baseAsset);

    if (markets.length === 0) {
      return null;
    }

    const quotes = await hyperliquidClient.getQuotes(markets);

    if (quotes.length === 0) {
      return null;
    }

    // Find best bid (highest price - best for sellers)
    const bestBid = quotes.reduce((best, current) =>
      current.bidPrice > best.bidPrice ? current : best
    );

    // Find best ask (lowest price - best for buyers)
    const bestAsk = quotes.reduce((best, current) =>
      current.askPrice < best.askPrice ? current : best
    );

    return {
      symbol: `${baseAsset}-PERP`,
      baseAsset,
      bestBid,
      bestAsk,
      allQuotes: quotes,
      timestamp: Date.now(),
    };
  }

  // Smart routing: determine best execution venue
  async getBestRoute(
    baseAsset: string,
    side: 'buy' | 'sell',
    size: number
  ): Promise<ExecutionRoute | null> {
    const aggregated = await this.aggregateQuotes(baseAsset);

    if (!aggregated) {
      return null;
    }

    // For buy orders, we want the best ask (lowest price)
    // For sell orders, we want the best bid (highest price)
    const bestQuote = side === 'buy' ? aggregated.bestAsk : aggregated.bestBid;
    const price = side === 'buy' ? bestQuote.askPrice : bestQuote.bidPrice;
    const availableSize = side === 'buy' ? bestQuote.askSize : bestQuote.bidSize;

    // Calculate expected slippage
    // Simplified: if requested size > available size, there will be slippage
    const expectedSlippage = size > availableSize ? size - availableSize : 0;

    // Estimate fees (typically 0.02% maker, 0.05% taker on Hyperliquid)
    const takerFeeBps = 5; // 5 basis points = 0.05%
    const fees = (price * size * takerFeeBps) / 10000;

    return {
      provider: bestQuote.provider,
      marketId: bestQuote.marketId,
      expectedPrice: price,
      expectedSlippage,
      fees,
      reason: `Best ${side} price: ${price} with ${availableSize} available`,
    };
  }

  // Compare execution across all venues
  async compareExecution(
    baseAsset: string,
    side: 'buy' | 'sell',
    size: number
  ): Promise<ExecutionRoute[]> {
    const aggregated = await this.aggregateQuotes(baseAsset);

    if (!aggregated) {
      return [];
    }

    const quotes = side === 'buy' ? aggregated.allQuotes : aggregated.allQuotes;

    return quotes
      .map((quote) => {
        const price = side === 'buy' ? quote.askPrice : quote.bidPrice;
        const availableSize = side === 'buy' ? quote.askSize : quote.bidSize;
        const expectedSlippage = size > availableSize ? size - availableSize : 0;
        const takerFeeBps = 5;
        const fees = (price * size * takerFeeBps) / 10000;

        return {
          provider: quote.provider,
          marketId: quote.marketId,
          expectedPrice: price,
          expectedSlippage,
          fees,
          reason: `Price: ${price}, Available: ${availableSize}`,
        };
      })
      .sort((a, b) => {
        // Sort by best price for the given side
        if (side === 'buy') {
          return a.expectedPrice - b.expectedPrice; // Lower is better for buys
        } else {
          return b.expectedPrice - a.expectedPrice; // Higher is better for sells
        }
      });
  }

  /**
   * Calculate smart routing for large orders across multiple venues
   * Splits order optimally based on available liquidity and pricing
   */
  async calculateSmartRoute(
    baseAsset: string,
    side: 'buy' | 'sell',
    size: number
  ): Promise<SmartRoute | null> {
    const aggregated = await this.aggregateQuotes(baseAsset);

    if (!aggregated || size <= 0) {
      return null;
    }

    // Sort venues by best price for the given side
    const sortedQuotes = [...aggregated.allQuotes].sort((a, b) => {
      const priceA = side === 'buy' ? a.askPrice : a.bidPrice;
      const priceB = side === 'buy' ? b.askPrice : b.bidPrice;

      if (side === 'buy') {
        return priceA - priceB; // Lower is better for buys
      } else {
        return priceB - priceA; // Higher is better for sells
      }
    });

    // Greedily fill order from best to worst price
    const splits: OrderSplit[] = [];
    let remainingSize = size;
    let totalNotional = 0;

    for (const quote of sortedQuotes) {
      if (remainingSize <= 0) break;

      const price = side === 'buy' ? quote.askPrice : quote.bidPrice;
      const availableSize = side === 'buy' ? quote.askSize : quote.bidSize;

      // Take as much as available or as much as needed
      const fillSize = Math.min(remainingSize, availableSize);

      if (fillSize > 0) {
        splits.push({
          provider: quote.provider,
          marketId: quote.marketId,
          size: fillSize,
          expectedPrice: price,
          percentage: (fillSize / size) * 100,
        });

        totalNotional += fillSize * price;
        remainingSize -= fillSize;
      }
    }

    // If we couldn't fill the entire order, still return what we can do
    const filledSize = size - remainingSize;
    const weightedAvgPrice = filledSize > 0 ? totalNotional / filledSize : 0;

    // Calculate total fees (0.05% taker fee)
    const takerFeeBps = 5;
    const totalFees = (totalNotional * takerFeeBps) / 10000;

    // Estimate slippage (difference from best price)
    const bestPrice = side === 'buy' ? aggregated.bestAsk.askPrice : aggregated.bestBid.bidPrice;
    const slippageBps = Math.abs((weightedAvgPrice - bestPrice) / bestPrice) * 10000;

    return {
      totalSize: filledSize,
      splits,
      weightedAvgPrice,
      totalFees,
      estimatedSlippage: slippageBps,
      totalNotional,
      reason: splits.length > 1
        ? `Split across ${splits.length} venues for optimal execution`
        : `Single venue execution on ${splits[0]?.provider || 'unknown'}`,
    };
  }
}

export const quoteAggregator = new QuoteAggregator();
